import { PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  signalStore,
  withState,
  withMethods,
  withComputed,
  patchState,
} from '@ngrx/signals';
import { computed } from '@angular/core';

export type NotificationType = 'info' | 'success' | 'warning' | 'error';

export interface Notification {
  id: number;
  message: string;
  type: NotificationType;
}

export interface WebSocketNotification {
  notificationId: string;
  type: string;
  title: string;
  message: string;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  relatedEntityId?: string;
  relatedEntityType?: string;
  data?: any;
  timestamp: string;
  action?: string;
  read?: boolean;
}

// État initial du store
const initialState = {
  notifications: [] as Notification[],
  notificationsList: [] as WebSocketNotification[],
  unreadCount: 0,
};

let nextId = 0;

/**
 * Service (Store) de gestion des notifications pour Alienala
 * Utilise @ngrx/signals pour un état réactif sans Observables.
 */
export const NotificationService = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    unreadNotifications: computed(() =>
      store
        .notificationsList()
        .filter(
          (notif) => notif.priority !== 'URGENT' && notif.priority !== 'HIGH',
        ),
    ),
  })),
  withMethods((store, platformId = inject(PLATFORM_ID)) => {
    const remove = (id: number) => {
      patchState(store, (state) => ({
        notifications: state.notifications.filter((n) => n.id !== id),
      }));
    };

    const add = (
      message: string,
      type: NotificationType = 'info',
      duration = 5000,
    ) => {
      const id = nextId++;
      const newNotification: Notification = { id, message, type };

      patchState(store, (state) => ({
        notifications: [...state.notifications, newNotification],
      }));

      if (duration > 0 && isPlatformBrowser(platformId)) {
        setTimeout(() => remove(id), duration);
      }
    };

    const handleNotificationAction = (notification: WebSocketNotification) => {
      if (notification.action && isPlatformBrowser(platformId)) {
        window.dispatchEvent(
          new CustomEvent('alienala-notification-action', {
            detail: {
              action: notification.action,
              relatedEntityId: notification.relatedEntityId,
              relatedEntityType: notification.relatedEntityType,
            },
          }),
        );
      }
    };

    const showBrowserNotification = (notification: WebSocketNotification) => {
      if (
        isPlatformBrowser(platformId) &&
        'Notification' in window &&
        Notification.permission === 'granted'
      ) {
        const browserNotif = new Notification(notification.title, {
          body: notification.message,
          icon: 'icons/notification.png',
          badge: 'icons/badge.png',
          tag: notification.notificationId,
          requireInteraction: notification.priority === 'URGENT',
        });

        browserNotif.onclick = () => {
          window.focus();
          handleNotificationAction(notification);
          browserNotif.close();
        };
      }
    };

    const playSound = (priority: string) => {
      if (!isPlatformBrowser(platformId)) return;
      try {
        const soundFile =
          priority === 'URGENT' || priority === 'HIGH'
            ? 'sounds/urgent.mp3'
            : 'sounds/notification.mp3';
        const audio = new Audio(soundFile);
        audio.volume = 0.5;
        audio.play().catch((err) => {
          console.log('Could not play sound:', err);
        });
      } catch (error) {
        console.log('Sound not available');
      }
    };

    const mapNotificationTypeToToast = (
      type: string,
      priority: string,
    ): NotificationType => {
      if (priority === 'URGENT' || priority === 'HIGH') return 'error';
      if (type.includes('ERROR') || type.includes('FAILED')) return 'error';
      if (
        type.includes('SUCCESS') ||
        type.includes('READY') ||
        type.includes('COMPLETED')
      )
        return 'success';
      if (type.includes('WARNING') || type.includes('DELAYED'))
        return 'warning';
      return 'info';
    };

    const showToast = (notification: WebSocketNotification) => {
      const toastType = mapNotificationTypeToToast(
        notification.type,
        notification.priority,
      );
      const displayMessage = notification.title
        ? `${notification.title}: ${notification.message}`
        : notification.message;
      add(displayMessage, toastType, 5000);
    };

    return {
      add,
      remove,
      handleWebSocketNotification(notification: WebSocketNotification) {
        console.log('📬 Alienala notification received:', notification);

        if (
          store
            .notificationsList()
            .some((item) => item.notificationId === notification.notificationId)
        ) {
          return;
        }

        patchState(store, (state) => ({
          notificationsList: [
            { ...notification, read: false },
            ...state.notificationsList,
          ],
          unreadCount: state.unreadCount + 1,
        }));

        showToast(notification);
        playSound(notification.priority);
        showBrowserNotification(notification);
      },
      synchronizeNotifications(notifications: WebSocketNotification[]) {
        const realtimeById = new Map(
          store
            .notificationsList()
            .map((notification) => [notification.notificationId, notification]),
        );
        for (const notification of notifications) {
          if (!realtimeById.has(notification.notificationId)) {
            realtimeById.set(notification.notificationId, notification);
          }
        }
        const merged = [...realtimeById.values()].sort(
          (left, right) =>
            new Date(right.timestamp).getTime() -
            new Date(left.timestamp).getTime(),
        );
        patchState(store, {
          notificationsList: merged,
          unreadCount: merged.filter((notification) => !notification.read)
            .length,
        });
      },
      resetUnreadCount() {
        patchState(store, { unreadCount: 0 });
      },
      markAsRead(notificationId: string) {
        let wasUnread = false;
        patchState(store, (state) => {
          const notif = state.notificationsList.find(
            (n) => n.notificationId === notificationId,
          );
          if (notif && !notif.read) {
            wasUnread = true;
          }
          return {
            notificationsList: state.notificationsList.map((n) =>
              n.notificationId === notificationId ? { ...n, read: true } : n,
            ),
            unreadCount: wasUnread ? state.unreadCount - 1 : state.unreadCount,
          };
        });
      },
      markAllAsRead() {
        patchState(store, {
          notificationsList: store
            .notificationsList()
            .map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        });
      },
      deleteNotification(notificationId: string) {
        patchState(store, (state) => {
          const updatedList = state.notificationsList.filter(
            (n) => n.notificationId !== notificationId,
          );
          return {
            notificationsList: updatedList,
            unreadCount: state.unreadCount > 0 ? state.unreadCount - 1 : 0,
          };
        });
      },
      clearAll() {
        patchState(store, { notificationsList: [], unreadCount: 0 });
      },
      setUnreadCount(count: number) {
        patchState(store, { unreadCount: count });
      },
      requestNotificationPermission() {
        if (
          isPlatformBrowser(platformId) &&
          'Notification' in window &&
          Notification.permission === 'default'
        ) {
          Notification.requestPermission().then((permission) => {
            console.log('Notification permission:', permission);
          });
        }
      },
    };
  }),
);
